import { Modal, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import LocalExchanges, { exchangeSections, resolvePreferredExchanges, splitExchangeSection } from "data/exchanges";
import { updatePreferredExchanges } from "api/user";
import ExchangeSelectionTile, { exchangeSelectionGridSx } from "component/Exchanges/ExchangeSelectionTile";
import { useDispatch, useSelector } from "react-redux";
import { set_user } from "redux/reducer/user";
import { set_message } from "redux/reducer/message";

const sectionLabelSx = {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    borderBottom: '1px solid #fff',
    width: '100%',
    paddingBottom: '2px',
    marginBottom: 1,
};

const addLabelSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    mt: 1.5,
    mb: 0.75,
};

const PreferredExchanges = ({ toggleWallet, wallet, setWallet }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user?.user);
    const [selected, setSelected] = useState(() => resolvePreferredExchanges(wallet));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSelected(resolvePreferredExchanges(wallet));
    }, [wallet]);

    const toggleSelection = (name) => {
        setSelected((prev) =>
            prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
        );
    };

    const onConfirm = (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        const validSelection = selected.filter((name) => LocalExchanges.includes(name));
        setWallet(validSelection);
        updatePreferredExchanges(validSelection)
            .then(() => {
                if (user) {
                    dispatch(set_user({ ...user, preferredExchanges: validSelection }));
                }
                dispatch(set_message('Tools updated'));
                toggleWallet();
            })
            .catch((err) => {
                console.log(err);
                dispatch(set_message('Unable to update tools. Please try again.'));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Modal
            open={true}
            onClose={toggleWallet}
            aria-labelledby="preferred-exchanges-modal"
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: 1.5,
            }}
        >
            <Box sx={{
                position: 'relative',
                bgcolor: 'primary.light',
                width: '100%',
                maxWidth: '750px',
                maxHeight: '90vh',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                minHeight: 200,
                px: { xs: 2.5, sm: 5 },
                py: 4,
                borderRadius: 5,
            }}>
                <IconButton
                    type="button"
                    onClick={toggleWallet}
                    aria-label="Close tools settings"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        color: '#fff',
                        p: 0.5,
                        '&:hover': { bgcolor: 'transparent', opacity: 0.75 },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {exchangeSections.map((section, index) => {
                    const { selectedItems, availableItems } = splitExchangeSection(section, selected);
                    return (
                        <Box key={section.title} sx={{ width: '100%', marginTop: index === 0 ? 1 : 3 }}>
                            <Typography sx={sectionLabelSx}>{section.title}</Typography>

                            {selectedItems.length > 0 ? (
                                <Box sx={exchangeSelectionGridSx}>
                                    {selectedItems.map((name) => (
                                        <ExchangeSelectionTile
                                            key={`selected-${name}`}
                                            name={name}
                                            selected
                                            onToggle={toggleSelection}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, mt: 1 }}>
                                    No {section.title.toLowerCase()} selected yet.
                                </Typography>
                            )}

                            {availableItems.length > 0 && (
                                <>
                                    <Typography sx={addLabelSx}>Add</Typography>
                                    <Box sx={exchangeSelectionGridSx}>
                                        {availableItems.map((name) => (
                                            <ExchangeSelectionTile
                                                key={`add-${name}`}
                                                name={name}
                                                selected={false}
                                                onToggle={toggleSelection}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    );
                })}

                <Box sx={{ width: '100%', height: 1.1, background: '#fff', marginTop: 3 }} />
                <Box sx={{ display: 'block', marginTop: 2, marginLeft: 'auto' }}>
                    <Button
                        type="submit"
                        sx={{ color: '#fff', borderColor: '#fff', borderRadius: '20px', textTransform: 'capitalize' }}
                        variant="outlined"
                        onClick={onConfirm}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default PreferredExchanges;
